// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  CalendarDays,
  AlertCircle,
  Search,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Check,
  Tablet,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { apiFetch } from '@/lib/api'
import { formatDobDisplay } from '@/lib/date-format'
import { useAuth } from '@/context/auth-context'
import {
  INTAKE_STATUS_TONE,
  apptStatusTone,
  apptStatusKey,
  visitTypeKey,
  titleCaseCode,
  formatApptTime,
  intakeActionLabelKey,
  type IntakeStatus,
  type PatientAppointment,
} from '@/lib/encounter'

// GET /v1/appointments row: an encounter plus the patient-identity columns the
// worklist needs to render.
interface ApiAppointment extends PatientAppointment {
  patientId: string
  patientName: string
  patientDob: string
}

// GET /v1/appointments — a filtered, paginated page plus the full match count.
interface AppointmentsResponse {
  appointments: ApiAppointment[]
  total: number
  limit: number
  offset: number
}

type Status = 'loading' | 'done' | 'error'

const PAGE_SIZES = [25, 50, 100] as const
const DEFAULT_PAGE_SIZE = 25

// The worklist doubles as a live board: front desk watches the "Ready for provider" group
// for patients who just finished intake, so refetch silently on this interval.
const WORKLIST_POLL_MS = 15_000

// Appointment statuses a caller may filter on (mirrors the backend's allow-list).
const STATUS_OPTIONS = [
  'proposed', 'pending', 'booked', 'arrived',
  'checked_in', 'waitlist', 'fulfilled', 'cancelled', 'noshow',
] as const

const INTAKE_OPTIONS: IntakeStatus[] = ['not_sent', 'link_sent', 'in_progress', 'prep_ready', 'reviewed']

// ── date helpers ─────────────────────────────────────────────────────────────
// Windows are computed at CLINIC-tz midnight (not the viewer's), so the buttons
// match the backend's clinic-tz default — otherwise "Today" from an IST viewer and
// the backend's ET default disagree and drop/add a boundary appointment.
const CLINIC_TZ = 'America/New_York'
function startOfClinicDay(d: Date): Date {
  const ymd = new Intl.DateTimeFormat('en-CA', { timeZone: CLINIC_TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).format(d)
  const [y, m, day] = ymd.split('-').map(Number) as [number, number, number]
  const guess = new Date(Date.UTC(y, m - 1, day, 0, 0, 0))
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: CLINIC_TZ, hour12: false, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).formatToParts(guess)
  const g = (t: string) => Number(parts.find((p) => p.type === t)!.value)
  const asLocal = Date.UTC(g('year'), g('month') - 1, g('day'), g('hour') % 24, g('minute'))
  return new Date(guess.getTime() - (asLocal - guess.getTime()))
}
function addDays(d: Date, n: number): Date {
  const x = new Date(d)
  x.setDate(x.getDate() + n)
  return x
}
function sameLocalDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}
// The three dedicated date views.
type DateView = 'today' | 'upcoming' | 'history'

// [from, to) window for a dedicated view, in viewer-local day boundaries.
function viewWindow(view: DateView, today: Date): { from: Date; to: Date } {
  const s = startOfClinicDay(today)
  if (view === 'upcoming') return { from: addDays(s, 1), to: addDays(s, 8) }
  if (view === 'history') return { from: addDays(s, -30), to: s }
  return { from: s, to: addDays(s, 1) }
}

// Short "Mon D" day label, matching the table's time-cell date style.
function fmtDay(d: Date): string {
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(d)
}

// Caption for a [from, to) window: a single day collapses to one label, otherwise
// spans from the first to the last included day (to is exclusive → last day is to−1).
function windowLabel(from: Date, to: Date): string {
  const lastDay = addDays(to, -1)
  return sameLocalDay(from, lastDay) ? fmtDay(from) : `${fmtDay(from)} – ${fmtDay(lastDay)}`
}

// Split a comma-separated query value; empty → [].
function csv(v: string | null): string[] {
  return (v ?? '').split(',').map((s) => s.trim()).filter(Boolean)
}

// Whole-year age from an ISO date-of-birth; null when unparseable.
function ageFromDob(dob?: string): number | null {
  if (!dob) return null
  const d = new Date(dob)
  if (Number.isNaN(d.getTime())) return null
  const now = new Date()
  let age = now.getFullYear() - d.getFullYear()
  const m = now.getMonth() - d.getMonth()
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--
  return age >= 0 && age < 200 ? age : null
}

// ── Dropdown: a small state-driven popover (no portal) used for filters + menus.
function Dropdown({
  trigger,
  align = 'start',
  panelClassName,
  children,
}: {
  trigger: (o: { open: boolean; toggle: () => void }) => React.ReactNode
  align?: 'start' | 'end'
  panelClassName?: string
  children: React.ReactNode | ((o: { close: () => void }) => React.ReactNode)
}) {
  const [open, setOpen] = useState(false)
  const [dropUp, setDropUp] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  // Flip the panel above the trigger when there isn't room below (e.g. the last
  // table row), so it overlays rather than pushing/overflowing the container.
  const toggle = () => {
    if (!open && ref.current) {
      setDropUp(window.innerHeight - ref.current.getBoundingClientRect().bottom < 240)
    }
    setOpen((o) => !o)
  }
  useEffect(() => {
    if (!open) return
    function onDoc(e: MouseEvent) {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])
  return (
    <div ref={ref} className="relative">
      {trigger({ open, toggle })}
      {open && (
        <div
          className={cn(
            'absolute z-40 rounded-xl border border-border bg-card shadow-xl p-1.5',
            dropUp ? 'bottom-full mb-1.5' : 'top-full mt-1.5',
            align === 'end' ? 'right-0' : 'left-0',
            panelClassName,
          )}
        >
          {typeof children === 'function' ? children({ close: () => setOpen(false) }) : children}
        </div>
      )}
    </div>
  )
}

// Bordered pill trigger for a filter popover; tinted when a value is applied.
function FilterPill({
  label,
  count,
  active,
  open,
  onClick,
}: {
  label: string
  count?: number
  active: boolean
  open: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border text-[13px] font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        active
          ? 'border-primary/40 bg-primary/[0.06] text-foreground'
          : 'border-border bg-transparent text-muted-foreground hover:text-foreground hover:border-primary/40',
      )}
    >
      {label}
      {count ? (
        <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold tabular-nums">
          {count}
        </span>
      ) : null}
      <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} aria-hidden />
    </button>
  )
}

// Multi-select checkbox filter driven by an option list + selected values.
function MultiSelectFilter({
  label,
  options,
  selected,
  onToggle,
}: {
  label: string
  options: { value: string; label: string }[]
  selected: string[]
  onToggle: (value: string) => void
}) {
  return (
    <Dropdown
      panelClassName="w-56 max-h-72 overflow-y-auto"
      trigger={({ open, toggle }) => (
        <FilterPill label={label} count={selected.length} active={selected.length > 0} open={open} onClick={toggle} />
      )}
    >
      {options.length === 0 ? (
        <p className="px-3 py-2 text-[13px] text-muted-foreground">—</p>
      ) : (
        options.map((o) => {
          const checked = selected.includes(o.value)
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => onToggle(o.value)}
              className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] text-foreground hover:bg-secondary transition-colors text-left"
            >
              <span
                className={cn(
                  'h-4 w-4 rounded border flex items-center justify-center shrink-0',
                  checked ? 'bg-primary border-primary text-primary-foreground' : 'border-border',
                )}
              >
                {checked && <Check className="h-3 w-3" />}
              </span>
              <span className="truncate">{o.label}</span>
            </button>
          )
        })
      )}
    </Dropdown>
  )
}

export default function SchedulePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { actingAsOrg } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()

  const [appointments, setAppointments] = useState<ApiAppointment[]>([])
  const [total, setTotal] = useState(0)
  const [status, setStatus] = useState<Status>('loading')
  const [errorMsg, setErrorMsg] = useState('')

  // ── URL is the source of truth for every filter, the date window, and paging.
  const from = searchParams.get('from') ?? ''
  const to = searchParams.get('to') ?? ''
  // Primary date control: three dedicated windows.
  const rawView = searchParams.get('view')
  const view: DateView =
    rawView === 'upcoming' || rawView === 'history' ? rawView : 'today'
  const statusFilter = csv(searchParams.get('status'))
  const intakeFilter = csv(searchParams.get('intakeStatus'))
  const providerFilter = csv(searchParams.get('provider'))
  const statusKey = statusFilter.join(',')
  const intakeKey = intakeFilter.join(',')
  const qParam = searchParams.get('q') ?? ''
  const page = Math.max(0, Number(searchParams.get('page') ?? '0') || 0)
  // The rows-per-page UI offers the presets; the URL may carry any 1..100 value.
  const rawPageSize = Number(searchParams.get('pageSize'))
  const pageSize = Number.isFinite(rawPageSize) && rawPageSize >= 1 ? Math.min(rawPageSize, 100) : DEFAULT_PAGE_SIZE

  const [searchInput, setSearchInput] = useState(qParam)
  useEffect(() => { setSearchInput(qParam) }, [qParam])

  // Mutate the URL params; any filter edit resets to the first page.
  const setParam = useCallback(
    (mut: (p: URLSearchParams) => void, resetPage = true) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          mut(next)
          if (resetPage) next.delete('page')
          return next
        },
        { replace: false },
      )
    },
    [setSearchParams],
  )

  const toggleCsvParam = useCallback(
    (key: string, value: string) => {
      setParam((p) => {
        const cur = csv(p.get(key))
        const updated = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value]
        if (updated.length) p.set(key, updated.join(','))
        else p.delete(key)
      })
    },
    [setParam],
  )

  // Debounce the search box → the URL's q param (also drives the fetch).
  useEffect(() => {
    const id = setTimeout(() => {
      const trimmed = searchInput.trim()
      if (trimmed !== qParam) {
        setParam((p) => {
          if (trimmed) p.set('q', trimmed)
          else p.delete('q')
        })
      }
    }, 350)
    return () => clearTimeout(id)
  }, [searchInput, qParam, setParam])

  // The server query — provider is applied client-side, so it is NOT part of this
  // (changing the provider filter must not trigger a refetch).
  const serverQuery = useMemo(() => {
    const p = new URLSearchParams()
    if (from) p.set('from', from)
    if (to) p.set('to', to)
    if (statusFilter.length) p.set('status', statusFilter.join(','))
    if (intakeFilter.length) p.set('intakeStatus', intakeFilter.join(','))
    const qt = qParam.trim()
    if (qt.length >= 2) p.set('q', qt)
    p.set('limit', String(pageSize))
    p.set('offset', String(page * pageSize))
    return p.toString()
  }, [from, to, statusKey, intakeKey, qParam, page, pageSize])

  useEffect(() => {
    let cancelled = false
    let loadedOnce = false
    const load = async (silent: boolean) => {
      if (!silent) { setStatus('loading'); setErrorMsg('') }
      try {
        const res = await apiFetch(`/v1/appointments?${serverQuery}`)
        if (!res.ok) throw new Error(`Server returned ${res.status}`)
        const data = (await res.json()) as AppointmentsResponse
        if (cancelled) return
        setAppointments(data.appointments ?? [])
        setTotal(data.total ?? (data.appointments ?? []).length)
        setStatus('done')
        loadedOnce = true
      } catch (err) {
        // A silent-poll failure keeps the last-good board on screen rather than flashing an error.
        if (cancelled || loadedOnce) return
        setErrorMsg(err instanceof Error ? err.message : 'Unknown error')
        setStatus('error')
      }
    }
    void load(false)
    const iv = setInterval(() => void load(true), WORKLIST_POLL_MS)
    return () => { cancelled = true; clearInterval(iv) }
    // actingAsOrg keys the fetch so an operator switching orgs reloads the list.
  }, [serverQuery, actingAsOrg?.id])

  // Client-side provider refinement of the loaded page (see report: no practitioner
  // list endpoint / rows carry only providerName, so this narrows within the page).
  const visible = useMemo(
    () => (providerFilter.length ? appointments.filter((a) => providerFilter.includes(a.providerName || '')) : appointments),
    [appointments, providerFilter],
  )

  // Live worklist grouping: on the today/upcoming views, band the board by where each
  // encounter is in the intake — Ready for provider (prep_ready, actionable) at top, then
  // In progress (intake running at the kiosk right now), then everything else Scheduled —
  // so front desk sees state change the moment it lands. History stays flat.
  const groupReady = view !== 'history'
  const readyRows = useMemo(
    () => (groupReady ? visible.filter((a) => a.intakeStatus === 'prep_ready') : []),
    [visible, groupReady],
  )
  const inProgressRows = useMemo(
    () => (groupReady ? visible.filter((a) => a.intakeStatus === 'in_progress') : []),
    [visible, groupReady],
  )
  const otherRows = useMemo(
    () =>
      groupReady
        ? visible.filter((a) => a.intakeStatus !== 'prep_ready' && a.intakeStatus !== 'in_progress')
        : visible,
    [visible, groupReady],
  )

  // Provider options come from the distinct provider names in the current result set.
  const providerOptions = useMemo(() => {
    const names = new Set<string>()
    for (const a of appointments) if (a.providerName) names.add(a.providerName)
    return [...names].sort((x, y) => x.localeCompare(y)).map((n) => ({ value: n, label: n }))
  }, [appointments])

  // The date view is a primary control, not a filter — Clear resets the pill filters.
  const anyFilter =
    statusFilter.length > 0 || intakeFilter.length > 0 || providerFilter.length > 0 || qParam.trim().length > 0

  function clearFilters() {
    setSearchInput('')
    setParam((p) => {
      for (const k of ['status', 'intakeStatus', 'provider', 'q']) p.delete(k)
    })
  }

  // Friendly visit-type label: mapped code, else title-cased raw.
  function visitTypeLabel(code?: string): string | null {
    if (!code) return null
    const key = visitTypeKey(code)
    return key ? t(`visits.visitType.${key}`) : titleCaseCode(code)
  }

  const COLUMNS = [
    t('visits.colTime'),
    t('visits.colPatient'),
    t('visits.colVisitType'),
    t('visits.colReason'),
    t('visits.colProvider'),
    t('visits.colStatus'),
    t('visits.colIntakeStatus'),
    t('visits.colAction'),
  ]

  // Single per-row action: open the encounter detail page, where intake is launched.
  function renderActionCell(appt: ApiAppointment) {
    return (
      <div className="flex justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/patients/${appt.patientId}/encounters/${appt.appointmentId}`)}
        >
          <Tablet className="h-3.5 w-3.5" aria-hidden />
          {t(intakeActionLabelKey(appt.intakeStatus))}
        </Button>
      </div>
    )
  }

  // ── date control ─────────────────────────────────────────────────────────────
  const today = new Date()

  // Clicking a dedicated view rewrites view + [from, to) and (via setParam) resets page.
  function setView(v: 'today' | 'upcoming' | 'history') {
    const w = viewWindow(v, today)
    setParam((p) => {
      p.set('view', v)
      p.set('from', w.from.toISOString())
      p.set('to', w.to.toISOString())
    })
  }

  // Effective window (Dates) for the caption: dedicated views are computed.
  const effectiveWindow = viewWindow(view, today)
  const activeWindowLabel = windowLabel(effectiveWindow.from, effectiveWindow.to)

  const VIEW_BUTTONS = [
    { key: 'today', label: t('visits.view.today') },
    { key: 'upcoming', label: t('visits.view.upcoming') },
    { key: 'history', label: t('visits.view.history') },
  ] as const

  // One worklist row. Extracted so the flat list and the grouped (Ready-band) render
  // share identical markup; `i` drives the zebra stripe.
  function renderRow(appt: ApiAppointment, i: number) {
    const slot = formatApptTime(appt.scheduledAt, appt.clinicTimezone)
    const age = ageFromDob(appt.patientDob)
    const dob = formatDobDisplay(appt.patientDob)
    const typeLabel = visitTypeLabel(appt.appointmentType)
    const identity = age !== null ? `${dob} · ${t('visits.ageShort', { count: age })}` : dob
    return (
      <tr
        key={appt.appointmentId}
        className={cn('transition-colors hover:bg-secondary/50', i % 2 === 1 && 'bg-secondary/20', 'border-b border-border last:border-0')}
      >
        {/* Time (clinic timezone) */}
        <td className="px-3 py-3 whitespace-nowrap align-top">
          <span className="block text-[13px] font-medium text-foreground tabular-nums">{slot.time}</span>
          {slot.date && <span className="block text-[11px] text-muted-foreground tabular-nums">{slot.date}</span>}
        </td>
        {/* Patient — name + DOB · age */}
        <td className="px-3 py-3 align-top">
          <button
            onClick={() => navigate(`/patients/${appt.patientId}`)}
            title={appt.patientName}
            className="block text-left text-[13px] font-semibold text-foreground hover:text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded truncate max-w-[200px]"
          >
            {appt.patientName}
          </button>
          {identity && (
            <span className="block text-[11px] text-muted-foreground font-mono tabular-nums">{identity}</span>
          )}
        </td>
        {/* Visit type (dedicated column) */}
        <td className="px-3 py-3 align-top whitespace-nowrap">
          {typeLabel ? (
            <span className="inline-flex items-center rounded-full bg-secondary px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
              {typeLabel}
            </span>
          ) : (
            <span className="text-[13px] text-muted-foreground">—</span>
          )}
        </td>
        {/* Reason */}
        <td className="px-3 py-3 align-top">
          <span className="block text-[13px] text-foreground truncate max-w-[240px]" title={appt.reason || undefined}>
            {appt.reason || '—'}
          </span>
        </td>
        {/* Provider */}
        <td className="px-3 py-3 align-top text-[13px] text-muted-foreground">
          {appt.providerName || '—'}
        </td>
        {/* Appointment status pill */}
        <td className="px-3 py-3 align-top whitespace-nowrap">
          <span className={cn('inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-medium', apptStatusTone(appt.status))}>
            {appt.status
              ? t(`visits.apptStatus.${apptStatusKey(appt.status)}`, { defaultValue: titleCaseCode(appt.status) })
              : '—'}
          </span>
        </td>
        {/* Intake status pill */}
        <td className="px-3 py-3 align-top whitespace-nowrap">
          <span className={cn('inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-medium', INTAKE_STATUS_TONE[appt.intakeStatus])}>
            {t(`visits.status.${appt.intakeStatus}`)}
          </span>
        </td>
        {/* Action menu */}
        <td className="px-3 py-3 align-top">{renderActionCell(appt)}</td>
      </tr>
    )
  }

  // Full-width band that introduces a group inside the table (mirrors the pattern used
  // on the console calls list). `ready` is emphasized (accent) since it's the actionable one.
  function groupBand(kind: 'ready' | 'inprogress' | 'scheduled', count: number) {
    const label =
      kind === 'ready'
        ? t('visits.readyGroup', 'Ready for provider')
        : kind === 'inprogress'
          ? t('visits.inProgressGroup', 'In progress')
          : t('visits.scheduledGroup', 'Scheduled')
    const tone =
      kind === 'ready'
        ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-l-2 border-l-emerald-500'
        : kind === 'inprogress'
          ? 'bg-amber-500/10 text-amber-700 dark:text-amber-300 border-l-2 border-l-amber-500'
          : 'bg-secondary/60 text-muted-foreground'
    return (
      <tr key={`__band_${kind}`}>
        <td
          colSpan={COLUMNS.length}
          className={cn(
            'px-3 py-2 text-[11px] font-semibold uppercase tracking-wide border-b border-border',
            tone,
          )}
        >
          {label}
          <span className="ml-1.5 font-mono tabular-nums opacity-70">{count}</span>
        </td>
      </tr>
    )
  }

  // Segmented group of the three dedicated views; the active view is highlighted.
  const viewButtons = (
    <div className="inline-flex items-center rounded-lg border border-border p-0.5">
      {VIEW_BUTTONS.map((b) => {
        const active = view === b.key
        return (
          <button
            key={b.key}
            type="button"
            aria-pressed={active}
            onClick={() => setView(b.key)}
            className={cn(
              'px-3 h-8 rounded-md text-[13px] font-medium transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              active
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {b.label}
          </button>
        )
      })}
    </div>
  )

  // ── pagination ───────────────────────────────────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const rangeStart = total === 0 ? 0 : page * pageSize + 1
  const rangeEnd = page * pageSize + appointments.length

  const pageWindow = useMemo(() => {
    const win: number[] = []
    const start = Math.max(0, Math.min(page - 2, totalPages - 5))
    const end = Math.min(totalPages, Math.max(page + 3, 5))
    for (let i = start; i < end; i++) win.push(i)
    return win
  }, [page, totalPages])

  function goToPage(n: number) {
    setParam((p) => p.set('page', String(n)), false)
  }

  return (
    <div className="flex flex-col gap-5">
      {/* Page header */}
      <div className="flex items-center gap-3">
        <CalendarDays className="h-6 w-6 text-primary" />
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t('visits.pageTitle')}</h1>
          <p className="text-sm text-muted-foreground">{t('visits.pageSubtitle')}</p>
        </div>
      </div>

      {/* Toolbar — light, unboxed: view segmented group + caption over the filter row */}
      <div className="sticky top-0 z-20 rounded-2xl border border-border bg-card px-3.5 py-3 flex flex-col gap-2.5">
        <div className="flex flex-wrap items-center gap-2">
          {viewButtons}
          <span className="text-xs text-muted-foreground tabular-nums" aria-live="polite">
            {activeWindowLabel}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              type="search"
              className="h-9 pl-9"
              placeholder={t('visits.searchPlaceholder')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
          <MultiSelectFilter
            label={t('visits.filters.status')}
            options={STATUS_OPTIONS.map((s) => ({ value: s, label: t(`visits.apptStatus.${s}`) }))}
            selected={statusFilter}
            onToggle={(v) => toggleCsvParam('status', v)}
          />
          <MultiSelectFilter
            label={t('visits.filters.intakeStatus')}
            options={INTAKE_OPTIONS.map((s) => ({ value: s, label: t(`visits.status.${s}`) }))}
            selected={intakeFilter}
            onToggle={(v) => toggleCsvParam('intakeStatus', v)}
          />
          <MultiSelectFilter
            label={t('visits.filters.provider')}
            options={providerOptions}
            selected={providerFilter}
            onToggle={(v) => toggleCsvParam('provider', v)}
          />
          {anyFilter && (
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center gap-1 h-9 px-2.5 rounded-lg text-[13px] font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="h-3.5 w-3.5" />
              {t('visits.filters.clear')}
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-card rounded-2xl border border-border shadow-sm overflow-hidden">
        <div className="h-1 bg-gradient-to-r from-[#006EF9] via-[#02C7C3] to-[#006EF9]" />
        <div className="overflow-x-auto">
          <table className="w-full min-w-[960px]">
            <thead className="sticky top-0 z-10">
              <tr className="bg-secondary">
                {COLUMNS.map((h, i) => (
                  <th
                    key={h}
                    className={cn(
                      'font-mono text-[10px] font-semibold text-muted-foreground uppercase tracking-wide px-3 py-3 whitespace-nowrap',
                      i === COLUMNS.length - 1 ? 'text-right' : 'text-left',
                    )}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {status === 'loading' ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i} className="border-b border-border last:border-0">
                    {COLUMNS.map((_, j) => (
                      <td key={j} className="px-3 py-3">
                        <div className="h-4 rounded shimmer" style={{ width: j === 1 ? '78%' : '52%' }} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : status === 'error' ? (
                <tr>
                  <td colSpan={COLUMNS.length} className="px-6 py-12">
                    <div className="flex flex-col items-center gap-2 text-center">
                      <AlertCircle className="h-7 w-7 text-destructive" />
                      <p className="text-sm font-medium text-foreground">{t('visits.loadError')}</p>
                      <p className="text-xs text-muted-foreground">{errorMsg}</p>
                    </div>
                  </td>
                </tr>
              ) : visible.length === 0 ? (
                <tr>
                  <td colSpan={COLUMNS.length} className="px-6 py-14">
                    <div className="flex flex-col items-center gap-2 text-center">
                      <div className="h-11 w-11 rounded-xl bg-secondary flex items-center justify-center">
                        <CalendarDays className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <p className="text-sm font-medium text-foreground">{t('visits.noResults')}</p>
                      <p className="text-xs text-muted-foreground max-w-xs">
                        {anyFilter ? t('visits.noResultsFiltered') : t('visits.noResultsBody')}
                      </p>
                      {anyFilter && (
                        <Button variant="outline" size="sm" onClick={clearFilters} className="mt-1">
                          {t('visits.filters.clear')}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                <>
                  {readyRows.length > 0 && groupBand('ready', readyRows.length)}
                  {readyRows.map((appt, i) => renderRow(appt, i))}
                  {inProgressRows.length > 0 && groupBand('inprogress', inProgressRows.length)}
                  {inProgressRows.map((appt, i) => renderRow(appt, readyRows.length + i))}
                  {(readyRows.length > 0 || inProgressRows.length > 0) &&
                    otherRows.length > 0 &&
                    groupBand('scheduled', otherRows.length)}
                  {otherRows.map((appt, i) => renderRow(appt, readyRows.length + inProgressRows.length + i))}
                </>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination footer */}
        {status === 'done' && visible.length > 0 && (
          <div className="sticky bottom-0 flex flex-wrap items-center justify-between gap-3 border-t border-border bg-card px-4 py-3">
            <span className="text-[13px] text-muted-foreground tabular-nums">
              {t('visits.pagination.showing', { from: rangeStart, to: rangeEnd, total })}
            </span>
            <div className="flex items-center gap-3">
              <Dropdown
                align="end"
                panelClassName="w-28"
                trigger={({ open, toggle }) => (
                  <FilterPill label={t('visits.pagination.rowsPerPage', { count: pageSize })} active={false} open={open} onClick={toggle} />
                )}
              >
                {({ close }) =>
                  PAGE_SIZES.map((size) => (
                    <button
                      key={size}
                      className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-[13px] text-foreground hover:bg-secondary transition-colors text-left"
                      onClick={() => { close(); setParam((p) => p.set('pageSize', String(size))) }}
                    >
                      {size}
                      {size === pageSize && <Check className="h-3.5 w-3.5 text-primary" />}
                    </button>
                  ))
                }
              </Dropdown>
              <div className="flex items-center gap-1">
                <button
                  aria-label={t('visits.pagination.prev')}
                  disabled={page === 0}
                  onClick={() => goToPage(page - 1)}
                  className="h-8 w-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors disabled:opacity-40 disabled:pointer-events-none"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>
                {pageWindow.map((n) => (
                  <button
                    key={n}
                    onClick={() => goToPage(n)}
                    className={cn(
                      'h-8 min-w-8 px-2 rounded-lg text-[13px] font-medium tabular-nums transition-colors',
                      n === page ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-secondary',
                    )}
                  >
                    {n + 1}
                  </button>
                ))}
                <button
                  aria-label={t('visits.pagination.next')}
                  disabled={page >= totalPages - 1}
                  onClick={() => goToPage(page + 1)}
                  className="h-8 w-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors disabled:opacity-40 disabled:pointer-events-none"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
